import {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";

export type PrintoutVariableScope =
    | "hotel"
    | "guest"
    | "reservation"
    | "service"
    | "serviceCategory"
    | "sector"
    | "resource"
    | "resourceCategory"
    | "agenda"
    | "agendaItem"
    | "folio"
    | "folioItem"
    | "other";

export type PrintoutVariableKind =
    | "text"
    | "localizedText"
    | "number"
    | "currency"
    | "date"
    | "duration"
    | "time"
    | "boolean"
    | "list";

export const PRINTOUT_DATE_FORMATS = ["numeric", "short", "long", "weekday"] as const;
export type PrintoutDateFormat = typeof PRINTOUT_DATE_FORMATS[number];

export interface PrintoutVariableFormatting {
    dateFormat?: PrintoutDateFormat | null;
}

export const normalizePrintoutDateFormat = (value: unknown): PrintoutDateFormat | undefined => (
    typeof value === "string" && (PRINTOUT_DATE_FORMATS as readonly string[]).includes(value)
        ? value as PrintoutDateFormat
        : undefined
);

export type PrintoutVariableCompute =
    | "hotelAddress"
    | "hotelContactLine"
    | "hotelCountry"
    | "hotelLegalLine"
    | "guestAllergies"
    | "guestDietaryPreferences"
    | "guestGender"
    | "guestListName"
    | "guestNationality"
    | "guestPrimaryEmail"
    | "reservationFacilityNames"
    | "reservationResourceNames"
    | "reservationRoomCategoryNames"
    | "reservationRoomNames"
    | "reservationStaffNames"
    | "reservationTableNames"
    | "reservationRestaurantName"
    | "reservationGuestCount"
    | "reservationAdultCount"
    | "reservationTeenagerCount"
    | "reservationChildCount"
    | "reservationInfantCount"
    | "reservationGuestBreakdown"
    | "reservationDuration"
    | "reservationEatPeriod"
    | "reservationMealPlanDateRange"
    | "reservationStatus"
    | "resourceCategoryNames"
    | "agendaItemName"
    | "agendaItemTypeLabel"
    | "agendaItemSectorName"
    | "agendaItemStart"
    | "agendaItemEnd"
    | "agendaItemLocation"
    | "agendaItemTimeRange"
    | "serviceLocalizedDescription"
    | "serviceLocalizedShortDescription"
    | "serviceLocalizedName"
    | "servicePrice"
    | "servicePriceBeforeTax"
    | "serviceRateDescription"
    | "serviceRateName"
    | "serviceCategoryNames"
    | "folioBillToEmail"
    | "folioBillToName"
    | "folioBillToPhone"
    | "folioAmountDue"
    | "folioAmountPaid"
    | "folioAmountRemaining"
    | "folioFixedChargesTotal"
    | "folioFixedChargesTotalBeforeTax"
    | "folioItemCategoryName"
    | "folioItemDiscount"
    | "folioItemDuration"
    | "folioItemQuantity"
    | "folioItemTaxRate"
    | "folioReservationTotal"
    | "folioReservationTotalBeforeTax"
    | "folioStatus"
    | "folioTaxTotal"
    | "currentDate"
    | "currentTime"
    | "currentUserName"
    | "printedAt";

export interface PrintoutVariableAttr {
    path?: string;
    compute?: PrintoutVariableCompute;
    kind: PrintoutVariableKind;
    currencyPath?: string;
    nowrap?: boolean;
}

export interface PrintoutVariable {
    key: string;

    chipLabel: string;
    label: string;
    description: string;

    scope: PrintoutVariableScope;
    scopeLabel: string;
    attr: PrintoutVariableAttr;
}

export const DEFAULT_PRINTOUT_VARIABLE_FALLBACK = "-";
export const EMPTY_PRINTOUT_VARIABLE_FALLBACK = "";

// Named fallbacks are limited to entities that always exist.
const PRINTOUT_VARIABLE_DEFAULT_FALLBACKS: Record<string, string> = {
    currentUserFirstName: "Staff",
    currentUserName: "Staff",
    guestFamiliarName: "Guest",
    guestFirstName: "Guest",
    guestFullName: "Guest",
    guestListName: "Guest",
    guestLastName: "Guest",
    hotelName: "Hotel",
    resFacilityNames: "Location",
    resResourceNames: "Resource",
    sectorName: "Sector",
};

/** Optional details should disappear rather than print `-`. */
const EMPTY_DEFAULT_FALLBACK_VARIABLES = new Set<string>([
    "guestAcademicTitle",
    "guestAllergies",
    "guestBusinessTitle",
    "guestDietaryPreferences",
    "guestLastNamePrefix",
    "guestMiddleName",
    "guestNamePrefix",
    "guestNameSuffix",
    "hotelBic",
    "hotelCommercialRegisterNumber",
    "hotelIban",
    "hotelLegalLine",
    "hotelLegalName",
    "hotelVatId",
    "currentUserLastName",
    "resMealPlanDateRange",
    "resourceDescription",
    "serviceDescription",
    "serviceLocalizedDescription",
    "serviceLocalizedShortDescription",
    "serviceShortDescription",
]);

export const getPrintoutVariableDefaultFallback = (
    variable?: Pick<PrintoutVariable, "attr" | "key" | "scope">,
) => {
    if (!variable) return DEFAULT_PRINTOUT_VARIABLE_FALLBACK;

    if (EMPTY_DEFAULT_FALLBACK_VARIABLES.has(variable.key)) {
        return EMPTY_PRINTOUT_VARIABLE_FALLBACK;
    }

    return PRINTOUT_VARIABLE_DEFAULT_FALLBACKS[variable.key] ?? DEFAULT_PRINTOUT_VARIABLE_FALLBACK;
};

export const normalizePrintoutVariableFallback = (
    value: unknown,
    variable?: Pick<PrintoutVariable, "attr" | "key" | "scope">,
) => {
    return typeof value === "string"
        ? value
        : getPrintoutVariableDefaultFallback(variable);
};

export const HOTEL_PRINTOUT_VARIABLES = [
    {
        key: "hotelName",
        chipLabel: "hotelName",
        label: "Hotel name",
        description: "Name of the hotel.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.name",
            kind: "text",
        },
    },
    {
        key: "hotelEmail",
        chipLabel: "hotelEmail",
        label: "Hotel email",
        description: "Primary email address of the hotel.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.primaryEmail",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "hotelPhone",
        chipLabel: "hotelPhone",
        label: "Hotel phone",
        description: "Guest-contact phone number configured on the organization.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.guestContactPhone",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "hotelContactLine",
        chipLabel: "hotelContactLine",
        label: "Hotel contact line",
        description: "Phone and email joined with a separator, skipping whichever is missing.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            compute: "hotelContactLine",
            kind: "text",
        },
    },
    {
        key: "hotelAddress",
        chipLabel: "hotelAddress",
        label: "Hotel address",
        description: "Full hotel address.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            compute: "hotelAddress",
            kind: "text",
        },
    },
    {
        key: "hotelStreet",
        chipLabel: "hotelStreet",
        label: "Hotel street",
        description: "Street address of the hotel.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.address.addressLine1",
            kind: "text",
        },
    },
    {
        key: "hotelZip",
        chipLabel: "hotelZip",
        label: "Hotel zip",
        description: "Postal code of the hotel.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.address.postalCode",
            kind: "text",
        },
    },
    {
        key: "hotelCity",
        chipLabel: "hotelCity",
        label: "Hotel city",
        description: "City of the hotel.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.address.cityName",
            kind: "text",
        },
    },
    {
        key: "hotelState",
        chipLabel: "hotelState",
        label: "Hotel state",
        description: "State or region of the hotel.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.address.stateProv",
            kind: "text",
        },
    },
    {
        key: "hotelCountry",
        chipLabel: "hotelCountry",
        label: "Hotel country",
        description: "Country of the hotel.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            compute: "hotelCountry",
            kind: "text",
        },
    },
    {
        key: "hotelCountryCode",
        chipLabel: "hotelCountryCode",
        label: "Hotel country code",
        description: "Country code of the hotel.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.address.countryCode",
            kind: "text",
        },
    },
    {
        key: "hotelCurrency",
        chipLabel: "hotelCurrency",
        label: "Hotel currency",
        description: "Currency used by the hotel.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.currencyCode",
            kind: "text",
        },
    },
    // Optional legal/banking fields print nothing when absent.
    {
        key: "hotelLegalName",
        chipLabel: "hotelLegalName",
        label: "Hotel legal name",
        description: "Registered company name of the property, when it differs from the trading name. Use hotelName for the name on the letterhead; this is the one the invoice is issued under.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.legalName",
            kind: "text",
        },
    },
    {
        key: "hotelVatId",
        chipLabel: "hotelVatId",
        label: "Hotel VAT ID",
        description: "VAT identification number of the property (Austrian UID), without a label. Prints nothing when the property has none.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.vatId",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "hotelCommercialRegisterNumber",
        chipLabel: "hotelCommercialRegisterNumber",
        label: "Hotel commercial register number",
        description: "Commercial register number of the property (Austrian Firmenbuchnummer), without the FN label. Prints nothing when the property has none.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.commercialRegisterNumber",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "hotelIban",
        chipLabel: "hotelIban",
        label: "Hotel IBAN",
        description: "Bank account number of the property, as stored. Prints nothing when the property has none.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.iban",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "hotelBic",
        chipLabel: "hotelBic",
        label: "Hotel BIC",
        description: "BIC / SWIFT code of the property's bank. Prints nothing when the property has none.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            path: "organization.bic",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "hotelLegalLine",
        chipLabel: "hotelLegalLine",
        label: "Hotel legal line",
        description: "VAT ID, commercial register number, IBAN and BIC as one labelled footer line, skipping whichever is missing along with its separator. Use this for the legally required invoice footer instead of four chips with literal punctuation between them, which strands the separators when a field is absent.",
        scope: "hotel",
        scopeLabel: "Hotel",
        attr: {
            compute: "hotelLegalLine",
            kind: "text",
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const GUEST_PRINTOUT_VARIABLES = [
    {
        key: "guestFullName",
        chipLabel: "guestFullName",
        label: "Guest full name",
        description: "Full name of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.nameOrdered",
            kind: "text",
        },
    },
    {
        key: "guestListName",
        chipLabel: "guestListName",
        label: "Guest list name",
        description: "Guest name in list order: last name, first name, name prefix, academic title.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            compute: "guestListName",
            kind: "text",
        },
    },
    {
        key: "guestFirstName",
        chipLabel: "guestFirstName",
        label: "Guest first name",
        description: "First name of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.firstName",
            kind: "text",
        },
    },
    {
        key: "guestMiddleName",
        chipLabel: "guestMiddleName",
        label: "Guest middle name",
        description: "Middle name of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.middleName",
            kind: "text",
        },
    },
    {
        key: "guestLastName",
        chipLabel: "guestLastName",
        label: "Guest last name",
        description: "Last name of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.lastName",
            kind: "text",
        },
    },
    {
        key: "guestLastNamePrefix",
        chipLabel: "guestLastNamePrefix",
        label: "Guest last name prefix",
        description: "Last name prefix of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.lastNamePrefix",
            kind: "text",
        },
    },
    {
        key: "guestFamiliarName",
        chipLabel: "guestFamiliarName",
        label: "Guest familiar name",
        description: "Familiar name of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.familiarName",
            kind: "text",
        },
    },
    {
        key: "guestShortName",
        chipLabel: "guestShortName",
        label: "Guest short name",
        description: "Short name of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.shortname",
            kind: "text",
        },
    },
    {
        key: "guestNamePrefix",
        chipLabel: "guestNamePrefix",
        label: "Guest name prefix",
        description: "Name prefix of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.namePrefix",
            kind: "text",
        },
    },
    {
        key: "guestNameSuffix",
        chipLabel: "guestNameSuffix",
        label: "Guest name suffix",
        description: "Name suffix of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.nameSuffix",
            kind: "text",
        },
    },
    {
        key: "guestAcademicTitle",
        chipLabel: "guestAcademicTitle",
        label: "Guest academic title",
        description: "Academic title of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.academicTitle",
            kind: "text",
        },
    },
    {
        key: "guestBusinessTitle",
        chipLabel: "guestBusinessTitle",
        label: "Guest business title",
        description: "Business title of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.businessTitle",
            kind: "text",
        },
    },
    {
        key: "guestSalutation",
        chipLabel: "guestSalutation",
        label: "Guest salutation",
        description: "Salutation used when addressing the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.addressGreetings",
            kind: "text",
        },
    },
    {
        key: "guestLetterGreeting",
        chipLabel: "guestLetterGreeting",
        label: "Guest letter greeting",
        description: "Letter greeting used for the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.letterGreetings",
            kind: "text",
        },
    },
    {
        key: "guestEmail",
        chipLabel: "guestEmail",
        label: "Guest email",
        description: "Primary email address of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            compute: "guestPrimaryEmail",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "guestPhone",
        chipLabel: "guestPhone",
        label: "Guest phone",
        description: "Primary phone number of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.phones[0].phoneNumberFull",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "guestStreet",
        chipLabel: "guestStreet",
        label: "Guest street",
        description: "Street address of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.addresses[0].addressLine1",
            kind: "text",
        },
    },
    {
        key: "guestZip",
        chipLabel: "guestZip",
        label: "Guest zip",
        description: "Postal code of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.addresses[0].postalCode",
            kind: "text",
        },
    },
    {
        key: "guestCity",
        chipLabel: "guestCity",
        label: "Guest city",
        description: "City of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.addresses[0].cityName",
            kind: "text",
        },
    },
    {
        key: "guestState",
        chipLabel: "guestState",
        label: "Guest state",
        description: "State or region of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.addresses[0].stateProv",
            kind: "text",
        },
    },
    {
        key: "guestCountry",
        chipLabel: "guestCountry",
        label: "Guest country",
        description: "Country of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.addresses[0].countryName",
            kind: "text",
        },
    },
    {
        key: "guestCountryCode",
        chipLabel: "guestCountryCode",
        label: "Guest country code",
        description: "Country code of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.addresses[0].countryCode",
            kind: "text",
        },
    },
    {
        key: "guestGender",
        chipLabel: "guestGender",
        label: "Guest gender",
        description: "Gender of the guest, as a word (Male, Female, Non-binary).",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            compute: "guestGender",
            kind: "text",
        },
    },
    {
        key: "guestBirthDate",
        chipLabel: "guestBirthDate",
        label: "Guest birth date",
        description: "Birth date of the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.birthDay",
            kind: "date",
        },
    },
    {
        key: "guestNationality",
        chipLabel: "guestNationality",
        label: "Guest nationality",
        description: "Nationality of the guest, as a country name (United States, not US).",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            compute: "guestNationality",
            kind: "text",
        },
    },
    {
        key: "guestCompanyName",
        chipLabel: "guestCompanyName",
        label: "Guest company name",
        description: "Company name saved on the guest profile.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.company.companyName",
            kind: "text",
        },
    },
    {
        key: "guestCompanyVatCode",
        chipLabel: "guestCompanyVatCode",
        label: "Guest company VAT code",
        description: "VAT code saved on the guest company.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.company.vatCode",
            kind: "text",
        },
    },
    {
        key: "guestRoom",
        chipLabel: "guestRoom",
        label: "Guest room",
        description: "Current room assigned to the guest.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.currentStay.roomId",
            kind: "text",
        },
    },
    {
        key: "guestStayStartDate",
        chipLabel: "guestStayStartDate",
        label: "Guest stay start date",
        description: "Start date of the guest's current stay.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.currentStay.startDay",
            kind: "date",
        },
    },
    {
        key: "guestStayEndDate",
        chipLabel: "guestStayEndDate",
        label: "Guest stay end date",
        description: "End date of the guest's current stay.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            path: "profile.currentStay.endDay",
            kind: "date",
        },
    },
    // Physical printouts may expose food preferences, never CRQLAR_MEDICAL data.
    {
        key: "guestAllergies",
        chipLabel: "guestAllergies",
        label: "Guest food allergies",
        description: "Food allergies recorded on the guest profile, separated by commas. Prints nothing when none are recorded.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            compute: "guestAllergies",
            kind: "list",
        },
    },
    {
        key: "guestDietaryPreferences",
        chipLabel: "guestDietaryPrefs",
        label: "Guest dietary preferences",
        description: "Dietary preferences recorded on the guest profile (vegetarian, vegan, ...), separated by commas. Prints nothing when none are recorded.",
        scope: "guest",
        scopeLabel: "Guest",
        attr: {
            compute: "guestDietaryPreferences",
            kind: "list",
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const RESERVATION_PRINTOUT_VARIABLES = [
    {
        key: "resCode",
        chipLabel: "resCode",
        label: "Reservation code",
        description: "Booking or confirmation number.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.code",
            kind: "text",
        },
    },
    {
        key: "resStatus",
        chipLabel: "resStatus",
        label: "Reservation status",
        description: "Current reservation status, as a word (Confirmed, Cancelled, No show).",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationStatus",
            kind: "text",
        },
    },
    {
        key: "resMealPeriod",
        chipLabel: "resMealPeriod",
        label: "Reservation meal period",
        description: "Meal period saved on half-board restaurant reservations.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationEatPeriod",
            kind: "text",
        },
    },
    {
        key: "resMealPlanDateRange",
        chipLabel: "resMealPlanDateRange",
        label: "Meal plan date range",
        description: "First to last meal date in the saved meal plan. Uses all included meals, not just the reservation opened for printing.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationMealPlanDateRange",
            kind: "text",
        },
    },
    {
        key: "resMealStartDate",
        chipLabel: "resMealStartDate",
        label: "Meal start date",
        description: "Start date of the meal-period reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.expectedStartDate",
            kind: "date",
        },
    },
    {
        key: "resMealStartTime",
        chipLabel: "resMealStartTime",
        label: "Meal start time",
        description: "Start time of the meal-period reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.expectedStartTime",
            kind: "time",
            nowrap: true,
        },
    },
    {
        key: "resMealEndDate",
        chipLabel: "resMealEndDate",
        label: "Meal end date",
        description: "End date of the meal-period reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.expectedEndDate",
            kind: "date",
        },
    },
    {
        key: "resMealEndTime",
        chipLabel: "resMealEndTime",
        label: "Meal end time",
        description: "End time of the meal-period reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.expectedEndTime",
            kind: "time",
            nowrap: true,
        },
    },
    {
        key: "resStartDate",
        chipLabel: "resStartDate",
        label: "Reservation start date",
        description: "Start date of the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.expectedStartDate",
            kind: "date",
        },
    },
    {
        key: "resStartDay",
        chipLabel: "resStartDay",
        label: "Reservation start day",
        description: "Calendar day when the reservation starts.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.expectedStartDay",
            kind: "date",
        },
    },
    {
        key: "resStartTime",
        chipLabel: "resStartTime",
        label: "Reservation start time",
        description: "Start time of the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.expectedStartTime",
            kind: "time",
            nowrap: true,
        },
    },
    {
        key: "resEndDate",
        chipLabel: "resEndDate",
        label: "Reservation end date",
        description: "End date of the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.expectedEndDate",
            kind: "date",
        },
    },
    {
        key: "resEndDay",
        chipLabel: "resEndDay",
        label: "Reservation end day",
        description: "Calendar day when the reservation ends.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.expectedEndDay",
            kind: "date",
        },
    },
    {
        key: "resEndTime",
        chipLabel: "resEndTime",
        label: "Reservation end time",
        description: "End time of the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.expectedEndTime",
            kind: "time",
            nowrap: true,
        },
    },
    {
        key: "resActualStartDate",
        chipLabel: "resActualStartDate",
        label: "Reservation actual start date",
        description: "Actual start date recorded for the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.actualStartDate",
            kind: "date",
        },
    },
    {
        key: "resActualStartTime",
        chipLabel: "resActualStartTime",
        label: "Reservation actual start time",
        description: "Actual start time recorded for the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.actualStartTime",
            kind: "time",
            nowrap: true,
        },
    },
    {
        key: "resActualEndDate",
        chipLabel: "resActualEndDate",
        label: "Reservation actual end date",
        description: "Actual end date recorded for the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.actualEndDate",
            kind: "date",
        },
    },
    {
        key: "resActualEndTime",
        chipLabel: "resActualEndTime",
        label: "Reservation actual end time",
        description: "Actual end time recorded for the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.actualEndTime",
            kind: "time",
            nowrap: true,
        },
    },
    {
        key: "resRestaurantName",
        chipLabel: "resRestaurantName",
        label: "Reservation restaurant name",
        description: "Restaurant or parent resource assigned to the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationRestaurantName",
            kind: "list",
        },
    },
    {
        key: "resTableNames",
        chipLabel: "resTableNames",
        label: "Reservation table names",
        description: "Table names assigned to the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationTableNames",
            kind: "list",
        },
    },
    {
        key: "resRoomNames",
        chipLabel: "resRoomNames",
        label: "Reservation room names",
        description: "Room names or room numbers assigned to the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationRoomNames",
            kind: "list",
        },
    },
    {
        key: "resRoomCategoryNames",
        chipLabel: "resRoomCategories",
        label: "Reservation room categories",
        description: "Categories of the rooms assigned to the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationRoomCategoryNames",
            kind: "list",
        },
    },
    {
        key: "resResourceNames",
        chipLabel: "resResourceNames",
        label: "Reservation resource names",
        description: "Assigned resource names. For restaurant reservations this shows the assigned tables.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationResourceNames",
            kind: "list",
        },
    },
    {
        key: "resStaffNames",
        chipLabel: "resStaffNames",
        label: "Reservation staff names",
        description: "Assigned staff resource names, such as the staff member for an appointment.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationStaffNames",
            kind: "list",
        },
    },
    {
        key: "resFacilityNames",
        chipLabel: "resFacilityNames",
        label: "Reservation facility names",
        description: "Assigned non-staff resource or facility names, such as the room or treatment facility.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationFacilityNames",
            kind: "list",
        },
    },
    {
        key: "resGuestCount",
        chipLabel: "resGuestCount",
        label: "Reservation total guests",
        description: "Total number of guests.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationGuestCount",
            kind: "number",
        },
    },
    {
        key: "resAdultCount",
        chipLabel: "resAdultCount",
        label: "Reservation adults",
        description: "Number of adult guests.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationAdultCount",
            kind: "number",
        },
    },
    {
        key: "resTeenagerCount",
        chipLabel: "resTeenagerCount",
        label: "Reservation teenagers",
        description: "Number of teenager guests.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationTeenagerCount",
            kind: "number",
        },
    },
    {
        key: "resChildCount",
        chipLabel: "resChildCount",
        label: "Reservation children",
        description: "Number of child guests.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationChildCount",
            kind: "number",
        },
    },
    {
        key: "resInfantCount",
        chipLabel: "resInfantCount",
        label: "Reservation infants",
        description: "Number of infant guests.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationInfantCount",
            kind: "number",
        },
    },
    {
        key: "resGuestBreakdown",
        chipLabel: "resGuestBreakdown",
        label: "Reservation guest breakdown",
        description: "Guests grouped by age category.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationGuestBreakdown",
            kind: "list",
        },
    },
    {
        key: "resDuration",
        chipLabel: "resDuration",
        label: "Reservation duration",
        description: "Duration between the expected reservation start and end.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            compute: "reservationDuration",
            kind: "duration",
        },
    },
    {
        key: "resPrice",
        chipLabel: "resPrice",
        label: "Reservation price",
        description: "Localized price and currency from the first reservation rate.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.rateSnaps[0].price",
            kind: "currency",
        },
    },
    {
        key: "resPriceCurrency",
        chipLabel: "resPriceCurrency",
        label: "Reservation price currency",
        description: "Currency from the first reservation rate.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.rateSnaps[0].price.currency",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "resRateName",
        chipLabel: "resRateName",
        label: "Reservation rate name",
        description: "Name of the first reservation rate.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.rateSnaps[0].name",
            kind: "text",
        },
    },
    {
        key: "resRatePlanCode",
        chipLabel: "resRatePlanCode",
        label: "Reservation rate plan code",
        description: "Rate plan code of the first reservation rate.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.rateSnaps[0].ratePlanCode",
            kind: "text",
        },
    },
    {
        key: "resRateResourceName",
        chipLabel: "resRateResourceName",
        label: "Reservation rate resource name",
        description: "Resource name saved on the first reservation rate.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.rateSnaps[0].resourceName",
            kind: "text",
        },
    },
    {
        key: "resRateQuantity",
        chipLabel: "resRateQuantity",
        label: "Reservation rate quantity",
        description: "Quantity saved on the first reservation rate.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.rateSnaps[0].quantity",
            kind: "number",
        },
    },
    {
        key: "resCancellationDate",
        chipLabel: "resCancellationDate",
        label: "Reservation cancellation date",
        description: "Date when the reservation was cancelled.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.cancelledDate",
            kind: "date",
        },
    },
    {
        key: "resCancellationTime",
        chipLabel: "resCancellationTime",
        label: "Reservation cancellation time",
        description: "Time when the reservation was cancelled.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.cancelledDate",
            kind: "time",
        },
    },
    {
        key: "resCancellationReason",
        chipLabel: "resCancellationReason",
        label: "Reservation cancellation reason",
        description: "Reason saved for the reservation cancellation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.cancellationReason",
            kind: "text",
        },
    },
    {
        key: "resDeadlineDate",
        chipLabel: "resDeadlineDate",
        label: "Reservation deadline date",
        description: "Deadline date saved on the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.deadlineDate",
            kind: "date",
        },
    },
    {
        key: "resDeadlineTime",
        chipLabel: "resDeadlineTime",
        label: "Reservation deadline time",
        description: "Deadline time saved on the reservation.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.deadlineDate",
            kind: "time",
        },
    },
    {
        key: "resDeadlineDay",
        chipLabel: "resDeadlineDay",
        label: "Reservation deadline day",
        description: "Calendar day saved as the reservation deadline.",
        scope: "reservation",
        scopeLabel: "Reservation",
        attr: {
            path: "reservation.deadlineDay",
            kind: "date",
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const SERVICE_PRINTOUT_VARIABLES = [
    {
        key: "serviceName",
        chipLabel: "serviceName",
        label: "Service name",
        description: "Raw name of the booked service.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            path: "service.name",
            kind: "text",
        },
    },
    {
        key: "serviceLocalizedName",
        chipLabel: "serviceLocalizedName",
        label: "Service translated name",
        description: "Translated service name for the selected print language.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            compute: "serviceLocalizedName",
            kind: "text",
        },
    },
    {
        key: "serviceShortDescription",
        chipLabel: "serviceShortDescription",
        label: "Service short description",
        description: "Raw short summary of the booked service.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            path: "service.shortDescription",
            kind: "text",
        },
    },
    {
        key: "serviceLocalizedShortDescription",
        chipLabel: "serviceLocalizedShortDescription",
        label: "Service translated short description",
        description: "Translated short summary of the booked service for the selected print language.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            compute: "serviceLocalizedShortDescription",
            kind: "text",
        },
    },
    {
        key: "serviceDescription",
        chipLabel: "serviceDescription",
        label: "Service description",
        description: "Raw full description of the booked service.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            path: "service.description",
            kind: "text",
        },
    },
    {
        key: "serviceLocalizedDescription",
        chipLabel: "serviceLocalizedDescription",
        label: "Service translated description",
        description: "Translated full description of the booked service for the selected print language.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            compute: "serviceLocalizedDescription",
            kind: "text",
        },
    },
    {
        key: "serviceDurationMinutes",
        chipLabel: "serviceDurationMinutes",
        label: "Service duration",
        description: "Planned duration of the booked service.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            path: "service.durationMinutes",
            kind: "duration",
        },
    },
    {
        key: "serviceRateName",
        chipLabel: "serviceRateName",
        label: "Service rate name",
        description: "Name of the selected service rate in the print language.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            compute: "serviceRateName",
            kind: "text",
        },
    },
    {
        key: "serviceRateDescription",
        chipLabel: "serviceRateDescription",
        label: "Service rate description",
        description: "Description of the selected service rate in the print language.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            compute: "serviceRateDescription",
            kind: "text",
        },
    },
    {
        key: "servicePrice",
        chipLabel: "servicePrice",
        label: "Service price",
        description: "Price of the booked service, when available.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            compute: "servicePrice",
            kind: "currency",
        },
    },
    {
        key: "servicePriceBeforeTax",
        chipLabel: "servicePriceBeforeTax",
        label: "Service price before tax",
        description: "Price of the booked service before tax, when available.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            compute: "servicePriceBeforeTax",
            kind: "currency",
        },
    },
    {
        key: "servicePriceCurrency",
        chipLabel: "servicePriceCurrency",
        label: "Service price currency",
        description: "Currency used for the booked service price.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            path: "service.ratesExpanded[0].price.currency",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "serviceRateStartTime",
        chipLabel: "serviceRateStartTime",
        label: "Service rate start time",
        description: "Start time of the selected service rate.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            path: "service.ratesExpanded[0].startTime",
            kind: "time",
            nowrap: true,
        },
    },
    {
        key: "serviceRateEndTime",
        chipLabel: "serviceRateEndTime",
        label: "Service rate end time",
        description: "End time of the selected service rate.",
        scope: "service",
        scopeLabel: "Service",
        attr: {
            path: "service.ratesExpanded[0].endTime",
            kind: "time",
            nowrap: true,
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const SERVICE_CATEGORY_PRINTOUT_VARIABLES = [
    {
        key: "serviceCategoryName",
        chipLabel: "serviceCatName",
        label: "Service category name",
        description: "Name of the booked service category.",
        scope: "serviceCategory",
        scopeLabel: "Service category",
        attr: {
            compute: "serviceCategoryNames",
            kind: "list",
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const SECTOR_PRINTOUT_VARIABLES = [
    {
        key: "sectorName",
        chipLabel: "sectorName",
        label: "Sector name",
        description: "Name of the sector.",
        scope: "sector",
        scopeLabel: "Sector",
        attr: {
            path: "sector.name",
            kind: "text",
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const RESOURCE_PRINTOUT_VARIABLES = [
    {
        key: "resourceName",
        chipLabel: "resourceName",
        label: "Resource name",
        description: "Name of the selected resource.",
        scope: "resource",
        scopeLabel: "Resource",
        attr: {
            path: "resource.name",
            kind: "text",
        },
    },
    {
        key: "resourceLocalizedName",
        chipLabel: "resourceLocalizedName",
        label: "Resource translated name",
        description: "Translated resource name for the selected print language.",
        scope: "resource",
        scopeLabel: "Resource",
        attr: {
            path: "resource.nameLocalized",
            kind: "localizedText",
        },
    },
    {
        key: "resourceDescription",
        chipLabel: "resourceDescription",
        label: "Resource description",
        description: "Description of the selected resource in the print language.",
        scope: "resource",
        scopeLabel: "Resource",
        attr: {
            path: "resource.descriptionLocalized",
            kind: "localizedText",
        },
    },
    {
        key: "resourceCapacity",
        chipLabel: "resourceCapacity",
        label: "Resource capacity",
        description: "Guest capacity of the selected resource.",
        scope: "resource",
        scopeLabel: "Resource",
        attr: {
            path: "resource.capacity",
            kind: "number",
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const RESOURCE_CATEGORY_PRINTOUT_VARIABLES = [
    {
        key: "resourceCategoryName",
        chipLabel: "resourceCatName",
        label: "Resource category name",
        description: "Name of the assigned resource category.",
        scope: "resourceCategory",
        scopeLabel: "Resource category",
        attr: {
            compute: "resourceCategoryNames",
            kind: "list",
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const AGENDA_PRINTOUT_VARIABLES = [
    {
        key: "agendaStartDate",
        chipLabel: "agendaStartDate",
        label: "Agenda start date",
        description: "First day the agenda covers — the guest's arrival day, or the start of the printed range.",
        scope: "agenda",
        scopeLabel: "Agenda scope",
        attr: {
            path: "agendaStart",
            kind: "date",
        },
    },
    {
        key: "agendaEndDate",
        chipLabel: "agendaEndDate",
        label: "Agenda end date",
        description: "Last day the agenda covers — the guest's departure day, or the end of the printed range.",
        scope: "agenda",
        scopeLabel: "Agenda scope",
        attr: {
            path: "agendaEnd",
            kind: "date",
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const AGENDA_ITEM_PRINTOUT_VARIABLES = [
    {
        key: "agendaItemName",
        chipLabel: "agendaItemName",
        label: "Agenda item name",
        description: "What is booked: the service name (localized when available), falling back to the sector name for service-less bookings such as restaurant tables.",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            compute: "agendaItemName",
            kind: "text",
        },
    },
    {
        key: "agendaItemStart",
        chipLabel: "agendaItemStart",
        label: "Agenda item start",
        description: "Start as one value, e.g. 15.07.2026 14:30.",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            compute: "agendaItemStart",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "agendaItemEnd",
        chipLabel: "agendaItemEnd",
        label: "Agenda item end",
        description: "End as one value, e.g. 15.07.2026 15:30.",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            compute: "agendaItemEnd",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "agendaItemTimeRange",
        chipLabel: "agendaItemTimeRange",
        label: "Agenda item time",
        description: "Start and end as one time range, e.g. 15:00–16:00. The agenda's rows are already grouped by day, so this is the column a guest reads down.",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            compute: "agendaItemTimeRange",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "agendaItemLocation",
        chipLabel: "agendaItemLocation",
        label: "Agenda item location",
        description: "Where the booking is: the names of the resources assigned to it (a table, a treatment room, a court). Prints nothing when the booking has no resource assigned yet.",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            compute: "agendaItemLocation",
            kind: "list",
        },
    },
    {
        key: "agendaItemSector",
        chipLabel: "agendaItemSector",
        label: "Agenda item sector",
        description: "Name of the sector the booking belongs to (Spa, a restaurant's sector, ...).",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            compute: "agendaItemSectorName",
            kind: "text",
        },
    },
    {
        key: "agendaItemType",
        chipLabel: "agendaItemType",
        label: "Agenda item type",
        description: "Guest-facing reservation type: Spa, Dining or Experience.",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            compute: "agendaItemTypeLabel",
            kind: "text",
        },
    },
    {
        key: "agendaItemStartDate",
        chipLabel: "agendaItemStartDate",
        label: "Agenda item start date",
        description: "Start date only, e.g. 15.07.2026.",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            path: "agendaItem.start",
            kind: "date",
        },
    },
    {
        key: "agendaItemStartTime",
        chipLabel: "agendaItemStartTime",
        label: "Agenda item start time",
        description: "Start time only, e.g. 14:30.",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            path: "agendaItem.start",
            kind: "time",
            nowrap: true,
        },
    },
    {
        key: "agendaItemEndDate",
        chipLabel: "agendaItemEndDate",
        label: "Agenda item end date",
        description: "End date only, e.g. 15.07.2026.",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            path: "agendaItem.end",
            kind: "date",
        },
    },
    {
        key: "agendaItemEndTime",
        chipLabel: "agendaItemEndTime",
        label: "Agenda item end time",
        description: "End time only, e.g. 15:30.",
        scope: "agendaItem",
        scopeLabel: "Agenda item",
        attr: {
            path: "agendaItem.end",
            kind: "time",
            nowrap: true,
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const FOLIO_PRINTOUT_VARIABLES = [
    {
        key: "folioName",
        chipLabel: "folioName",
        label: "Folio name",
        description: "Name saved on the folio.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            path: "folio.name",
            kind: "text",
        },
    },
    {
        key: "folioInvoiceNumber",
        chipLabel: "folioInvoiceNumber",
        label: "Folio invoice number",
        description: "The folio's sequence number — the running number a receipt is filed and referred to by.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            path: "folio.sequenceId",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "folioStatus",
        chipLabel: "folioStatus",
        label: "Folio status",
        description: "Current folio status, humanized and localized (PAID → Paid / Bezahlt).",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioStatus",
            kind: "text",
        },
    },
    {
        key: "folioCurrency",
        chipLabel: "folioCurrency",
        label: "Folio currency",
        description: "Currency used by the folio.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            path: "folio.currency",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "folioBillToName",
        chipLabel: "folioBillToName",
        label: "Folio bill-to name",
        description: "Billing recipient name saved on the folio.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioBillToName",
            kind: "text",
        },
    },
    {
        key: "folioBillToEmail",
        chipLabel: "folioBillToEmail",
        label: "Folio bill-to email",
        description: "Billing recipient email saved on the folio.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioBillToEmail",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "folioBillToPhone",
        chipLabel: "folioBillToPhone",
        label: "Folio bill-to phone",
        description: "Billing recipient phone number saved on the folio.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioBillToPhone",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "folioTotal",
        chipLabel: "folioTotal",
        label: "Folio total",
        description: "Total gross amount of the folio.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            path: "folio.total",
            kind: "currency",
            nowrap: true,
        },
    },
    {
        key: "folioTotalBeforeTax",
        chipLabel: "folioTotalBeforeTax",
        label: "Folio total before tax",
        description: "Total amount before tax.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            path: "folio.total.amountBeforeTax",
            kind: "currency",
            currencyPath: "folio.total.currency",
            nowrap: true,
        },
    },
    {
        key: "folioTaxTotal",
        chipLabel: "folioTaxTotal",
        label: "Folio tax total",
        description: "Tax on the folio: the gross total minus the net total. Prints nothing unless the folio actually records both figures and they differ.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioTaxTotal",
            kind: "currency",
            nowrap: true,
        },
    },
    {
        key: "folioReservationTotal",
        chipLabel: "folioReservationTotal",
        label: "Folio reservation total",
        description: "Gross total of non-fixed folio charges.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioReservationTotal",
            kind: "currency",
            nowrap: true,
        },
    },
    {
        key: "folioReservationTotalBeforeTax",
        chipLabel: "folioResTotalBeforeTax",
        label: "Folio reservation total before tax",
        description: "Net total of non-fixed folio charges.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioReservationTotalBeforeTax",
            kind: "currency",
            nowrap: true,
        },
    },
    {
        key: "folioFixedChargesTotal",
        chipLabel: "folioFixedChargesTotal",
        label: "Folio fixed charges total",
        description: "Gross total of fixed folio charges.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioFixedChargesTotal",
            kind: "currency",
            nowrap: true,
        },
    },
    {
        key: "folioFixedChargesTotalBeforeTax",
        chipLabel: "folioFixedBeforeTax",
        label: "Folio fixed charges before tax",
        description: "Net total of fixed folio charges.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioFixedChargesTotalBeforeTax",
            kind: "currency",
            nowrap: true,
        },
    },
    {
        key: "folioAmountPaid",
        chipLabel: "folioAmountPaid",
        label: "Folio amount paid",
        description: "Amount already paid on the folio.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioAmountPaid",
            kind: "currency",
            nowrap: true,
        },
    },
    {
        key: "folioAmountDue",
        chipLabel: "folioAmountDue",
        label: "Folio amount due",
        description: "Amount due on the folio.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioAmountDue",
            kind: "currency",
            nowrap: true,
        },
    },
    {
        key: "folioAmountRemaining",
        chipLabel: "folioAmountRemaining",
        label: "Folio amount remaining",
        description: "Remaining amount on the folio.",
        scope: "folio",
        scopeLabel: "Folio",
        attr: {
            compute: "folioAmountRemaining",
            kind: "currency",
            nowrap: true,
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const FOLIO_ITEM_PRINTOUT_VARIABLES = [
    {
        key: "folioItemDescription",
        chipLabel: "folioItemDescription",
        label: "Folio item description",
        description: "Description of the folio line item.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.description",
            kind: "text",
        },
    },
    {
        key: "folioItemPriceDescription",
        chipLabel: "folioItemPriceDescription",
        label: "Folio item price description",
        description: "Price description saved on the folio line item.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.priceDescription",
            kind: "text",
        },
    },
    {
        key: "folioItemPrice",
        chipLabel: "folioItemPrice",
        label: "Folio item price",
        description: "Unit price of the folio line item.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.price",
            kind: "currency",
            nowrap: true,
        },
    },
    {
        key: "folioItemQuantity",
        chipLabel: "folioItemQuantity",
        label: "Folio item quantity",
        description: "Quantity of the folio line item.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            compute: "folioItemQuantity",
            kind: "number",
            nowrap: true,
        },
    },
    {
        key: "folioItemDiscount",
        chipLabel: "folioItemDiscount",
        label: "Folio item discount",
        description: "Discounts applied to the folio line item.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            compute: "folioItemDiscount",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "folioItemCategory",
        chipLabel: "folioItemCategory",
        label: "Folio item category",
        description: "Category of the folio line item.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            compute: "folioItemCategoryName",
            kind: "text",
        },
    },
    {
        key: "folioItemTaxRate",
        chipLabel: "folioItemTaxRate",
        label: "Folio item tax rate",
        description: "Tax rate used by the folio line item.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            compute: "folioItemTaxRate",
            kind: "text",
        },
    },
    {
        key: "folioItemTotal",
        chipLabel: "folioItemTotal",
        label: "Folio item total",
        description: "Total gross amount of the folio line item.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.totalAmount",
            kind: "currency",
            nowrap: true,
        },
    },
    {
        key: "folioItemTotalBeforeTax",
        chipLabel: "folioItemTotalBeforeTax",
        label: "Folio item total before tax",
        description: "Total amount before tax for the folio line item.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.totalAmount.amountBeforeTax",
            kind: "currency",
            currencyPath: "folioItem.totalAmount.currency",
            nowrap: true,
        },
    },
    {
        key: "folioItemStartDate",
        chipLabel: "folioItemStartDate",
        label: "Folio item start date",
        description: "Start date of the folio line item period.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.timeSpanStart",
            kind: "date",
            nowrap: true,
        },
    },
    {
        key: "folioItemStartTime",
        chipLabel: "folioItemStartTime",
        label: "Folio item start time",
        description: "Start time of the folio line item period.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.timeSpanStart",
            kind: "time",
            nowrap: true,
        },
    },
    {
        key: "folioItemEndDate",
        chipLabel: "folioItemEndDate",
        label: "Folio item end date",
        description: "End date of the folio line item period.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.timeSpanEnd",
            kind: "date",
            nowrap: true,
        },
    },
    {
        key: "folioItemEndTime",
        chipLabel: "folioItemEndTime",
        label: "Folio item end time",
        description: "End time of the folio line item period.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.timeSpanEnd",
            kind: "time",
            nowrap: true,
        },
    },
    {
        key: "folioItemDuration",
        chipLabel: "folioItemDuration",
        label: "Folio item duration",
        description: "Duration of the folio line item period.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            compute: "folioItemDuration",
            kind: "duration",
            nowrap: true,
        },
    },
    {
        key: "folioItemCode",
        chipLabel: "folioItemCode",
        label: "Folio item code",
        description: "Inventory or service code saved on the folio line item.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.serviceInventoryCode",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "folioItemInclusive",
        chipLabel: "folioItemInclusive",
        label: "Folio item inclusive",
        description: "Whether the folio line item is included in another price.",
        scope: "folioItem",
        scopeLabel: "Folio item",
        attr: {
            path: "folioItem.inclusive",
            kind: "boolean",
            nowrap: true,
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const OTHER_PRINTOUT_VARIABLES = [
    {
        key: "currentDate",
        chipLabel: "currentDate",
        label: "Current date",
        description: "Date when the document is created.",
        scope: "other",
        scopeLabel: "Other",
        attr: {
            compute: "currentDate",
            kind: "date",
        },
    },
    {
        key: "currentTime",
        chipLabel: "currentTime",
        label: "Current time",
        description: "Time when the document is created.",
        scope: "other",
        scopeLabel: "Other",
        attr: {
            compute: "currentTime",
            kind: "time",
        },
    },
    {
        key: "printedAt",
        chipLabel: "printedAt",
        label: "Printed at",
        description: "Date and time the document was printed, as one value, e.g. 15.07.2026 14:30.",
        scope: "other",
        scopeLabel: "Other",
        attr: {
            compute: "printedAt",
            kind: "text",
            nowrap: true,
        },
    },
    {
        key: "currentUserName",
        chipLabel: "currentUserName",
        label: "Current user name",
        description: "Full name of the signed-in user.",
        scope: "other",
        scopeLabel: "Other",
        attr: {
            compute: "currentUserName",
            kind: "text",
        },
    },
    {
        key: "currentUserFirstName",
        chipLabel: "currentUserFirstName",
        label: "Current user first name",
        description: "First name of the signed-in user.",
        scope: "other",
        scopeLabel: "Other",
        attr: {
            path: "user.firstName",
            kind: "text",
        },
    },
    {
        key: "currentUserLastName",
        chipLabel: "currentUserLastName",
        label: "Current user last name",
        description: "Last name of the signed-in user.",
        scope: "other",
        scopeLabel: "Other",
        attr: {
            path: "user.lastName",
            kind: "text",
        },
    },
] as const satisfies readonly PrintoutVariable[];

export const PRINTOUT_VARIABLES = [
    ...HOTEL_PRINTOUT_VARIABLES,
    ...GUEST_PRINTOUT_VARIABLES,
    ...RESERVATION_PRINTOUT_VARIABLES,
    ...SERVICE_PRINTOUT_VARIABLES,
    ...SERVICE_CATEGORY_PRINTOUT_VARIABLES,
    ...SECTOR_PRINTOUT_VARIABLES,
    ...RESOURCE_PRINTOUT_VARIABLES,
    ...RESOURCE_CATEGORY_PRINTOUT_VARIABLES,
    ...AGENDA_PRINTOUT_VARIABLES,
    ...AGENDA_ITEM_PRINTOUT_VARIABLES,
    ...FOLIO_PRINTOUT_VARIABLES,
    ...FOLIO_ITEM_PRINTOUT_VARIABLES,
    ...OTHER_PRINTOUT_VARIABLES,
] as const satisfies readonly PrintoutVariable[];

export type PrintoutVariableDefinition = PrintoutVariable;
export type PrintoutVariableKey = typeof PRINTOUT_VARIABLES[number]["key"];

const ROW_ONLY_PRINTOUT_VARIABLE_SCOPES: readonly PrintoutVariableScope[] = ["folioItem", "agendaItem"];

// Keep aligned with the context assembled by each print page.
const RESERVATION_PRINTOUT_VARIABLE_SCOPES: readonly PrintoutVariableScope[] = [
    "hotel",
    "guest",
    "reservation",
    "service",
    "serviceCategory",
    "sector",
    "resource",
    "resourceCategory",
    "other",
];

const PRINTOUT_TYPE_RESOLVABLE_SCOPES: Record<PrintoutType, readonly PrintoutVariableScope[] | null> = {
    [PrintoutType.RESERVATION_CONFIRMATION]: RESERVATION_PRINTOUT_VARIABLE_SCOPES,
    [PrintoutType.RESERVATION_CANCELLATION]: RESERVATION_PRINTOUT_VARIABLE_SCOPES,
    [PrintoutType.FOLIO_CONFIRMATION]: [
        "hotel",
        "guest",
        "reservation",
        "sector",
        "folio",
        "other",
    ],
    [PrintoutType.AGENDA_HANDOUT]: [
        "hotel",
        "guest",
        "reservation",
        "sector",
        "agenda",
        "other",
    ],
};

export const getPrintoutVariablesForType = (
    type?: PrintoutType | null,
): readonly PrintoutVariableDefinition[] => {
    const resolvableScopes = type ? PRINTOUT_TYPE_RESOLVABLE_SCOPES[type] : null;

    return PRINTOUT_VARIABLES.filter((variable) => (
        !ROW_ONLY_PRINTOUT_VARIABLE_SCOPES.includes(variable.scope)
        && (!resolvableScopes || resolvableScopes.includes(variable.scope))
    ));
};

export const PRINTOUT_VARIABLE_BY_KEY = PRINTOUT_VARIABLES.reduce((variablesByKey, variable) => {
    variablesByKey[variable.key] = variable;
    return variablesByKey;
}, {} as Record<PrintoutVariableKey, PrintoutVariableDefinition>);

export const findPrintoutVariable = (key: string): PrintoutVariableDefinition | undefined => {
    return PRINTOUT_VARIABLE_BY_KEY[key as PrintoutVariableKey];
};

export const parsePrintoutVariableReference = (reference: string) => {
    const [key, language] = reference.split(":");
    return {
        key,
        language: language || undefined,
    };
};

export const getPrintoutVariableDisplayReference = (reference: string) => {
    const parsed = parsePrintoutVariableReference(reference);
    const variable = findPrintoutVariable(parsed.key);

    if (!variable) return reference;

    return parsed.language ? `${variable.key}:${parsed.language}` : variable.key;
};

export const getPrintoutVariableChipLabel = (reference: string) => {
    const parsed = parsePrintoutVariableReference(reference);
    const variable = findPrintoutVariable(parsed.key);
    if (!variable) return reference;

    return parsed.language ? `${variable.chipLabel}:${parsed.language}` : variable.chipLabel;
};
